import { useEffect, useRef } from "react";
import "./detailDrawer.css";

/**
 * Reusable right-side slide-over drawer for dense backoffice pages (tables,
 * org charts). Renders nothing while closed - a permanent side panel would
 * compress the main table/canvas, so a dense-data page must get its full
 * content width back the moment nothing is selected. Detail only appears
 * after a row/node is clicked, as an overlay that never reflows the page
 * underneath it.
 *
 * The dimmed backdrop is purely visual (`pointer-events: none`) - a real
 * click-blocking backdrop would also block clicks on the table/canvas
 * underneath, which breaks "click another row/node while the drawer is
 * open updates its content." Closing on an outside click is instead
 * handled by a document click listener that explicitly ignores clicks on
 * `selectableSelector` (the host page's row/node class) and lets that
 * element's own onClick manage the selection change. This matters beyond
 * style: an earlier capture-phase version of this listener called
 * onClose() synchronously *before* the click reached its target, which
 * (outside React's batching) forced an immediate re-render that collapsed
 * the org-chart tier and removed the very node being clicked from the DOM
 * mid-dispatch - so its onClick handler never fired and the drawer just
 * stayed closed instead of switching to the new selection.
 *
 * `selectableSelector` must match every element that can OPEN this drawer
 * from closed, not just elements that switch the selection while it's
 * already open (Prompt 090 regression: a standalone "create" button that
 * opened the drawer but didn't match selectableSelector was outside the
 * panel and didn't match the row selector either, so the very click that
 * opened it also satisfied this same-tick outside-click check and closed
 * it again - reproducible in a real browser, though not in jsdom/Testing
 * Library, where effect timing relative to native event bubbling differs
 * enough that the race never lines up). Use a CSS selector list (e.g.
 * `"tr.wx-row, .wx-create-button"`) when more than one trigger exists.
 */
export default function DetailDrawer({ open, onClose, labelledBy, children, selectableSelector, panelClassName = "" }) {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        onClose();
      }
    }

    function handleOutsideClick(event) {
      if (panelRef.current && panelRef.current.contains(event.target)) {
        return;
      }
      if (selectableSelector && event.target.closest?.(selectableSelector)) {
        // A row/node click - its own onClick updates the selection and the
        // drawer stays open with new content; don't fight it here.
        return;
      }
      // An overlay opened FROM this drawer that deliberately renders outside
      // its DOM - a React portal to document.body - is still part of what the
      // operator is doing, so a click inside one is not a click outside the
      // drawer. Without this, P164-R5's printable-forms overlay (portalled so
      // its sheets escape the shell's fixed-height clipping when printing) had
      // its own close button tear down the whole stack: this listener is on
      // `document` in the CAPTURE phase, so it runs before the event ever
      // reaches the overlay and no handler in there can stop it. Overlays opt
      // in by marking their root `data-wx-overlay`.
      if (event.target.closest?.("[data-wx-overlay]")) {
        return;
      }
      onClose();
    }

    document.addEventListener("keydown", handleKeyDown);
    // Capture phase, not bubble: a click on a button *inside* the panel
    // (e.g. a drill-in "+ add" button that swaps the drawer's own view) can
    // trigger a React re-render that replaces that button's DOM node before
    // a bubble-phase document listener would ever see the event. At that
    // point `event.target` is a node already detached from the document, so
    // `panelRef.current.contains(event.target)` wrongly returns false and
    // this handler closes the drawer that the click was meant to keep open
    // (reproducible in a real browser; does not reproduce in jsdom/Testing
    // Library, where this exact timing never lines up - see this file's own
    // top-of-file Prompt 090 note for the same underlying race in a
    // different shape). Listening on the capture phase runs this check
    // before the click ever reaches its target, while `event.target` is
    // still the real, attached, originally-clicked element.
    document.addEventListener("click", handleOutsideClick, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("click", handleOutsideClick, true);
    };
  }, [open, onClose, selectableSelector]);

  if (!open) {
    return null;
  }

  return (
    <div className="wx-drawer-root">
      <div className="wx-drawer-backdrop" aria-hidden="true" />
      {/* panelClassName widens the panel for a host page whose drawer holds a
          real form rather than a read-only summary (the employer record). */}
      <div ref={panelRef} className={`wx-drawer-panel ${panelClassName}`.trim()} role="dialog" aria-modal="true" aria-labelledby={labelledBy}>
        <button type="button" className="wx-drawer-close" onClick={onClose} aria-label="ปิดรายละเอียด">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M18 6 6 18" />
            <path d="M6 6l12 12" />
          </svg>
        </button>
        <div className="wx-drawer-body">{children}</div>
      </div>
    </div>
  );
}
